'use client';

import { useRouter } from 'next/navigation';
import type { Dispatch, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import UnsavedChangesWarning from '@/app/components/UnsavedChangesWarning';

const NavigationBlockerContext = createContext<
  [isBlocked: boolean, setBlocked: Dispatch<SetStateAction<boolean>>]
>([false, () => {}]);

export function NavigationBlockerProvider({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  const state = useState(false);
  return (
    <NavigationBlockerContext.Provider value={state}>
      {children}
    </NavigationBlockerContext.Provider>
  );
}

export function useIsBlocked() {
  const [isBlocked, setIsBlocked] = useContext(NavigationBlockerContext);
  return { isBlocked, setIsBlocked };
}

export function Blocker({ isBlocked }: { readonly isBlocked: boolean }) {
  const [, setBlocked] = useContext(NavigationBlockerContext);

  useEffect(() => {
    setBlocked(isBlocked);
    return () => {
      setBlocked(false);
    };
  }, [isBlocked, setBlocked]);

  return null;
}

export function BlockBrowserNavigation({
  backHref,
}: {
  readonly backHref?: string;
}) {
  const { isBlocked } = useIsBlocked();
  const router = useRouter();
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  useEffect(() => {
    if (!isBlocked) {
      setIsWarningOpen(false);
    }
  }, [isBlocked]);

  useEffect(() => {
    // Used to make popstate event trigger when back button is clicked.
    // Without this, the popstate event will not fire because it needs there to be a href to return
    if (typeof globalThis !== 'undefined' && isBlocked) {
      globalThis.history.pushState(
        null,
        document.title,
        globalThis.location.href,
      );
    }
  }, [isBlocked]);

  useEffect(() => {
    if (!isBlocked) return;

    // Used to prevent navigation when `back` browser button is clicked
    const handlePopState = () => {
      globalThis.history.pushState(
        null,
        document.title,
        globalThis.location.href,
      );
      setIsWarningOpen(true);
    };
    // Used to prevent navigation when reload page or navigate to another page
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    // capture run before other listeners that may consume back navigation (e.g. Next.js 16).
    globalThis.addEventListener('popstate', handlePopState, true);
    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
      globalThis.removeEventListener('popstate', handlePopState, true);
    };
  }, [isBlocked]);

  return (
    <UnsavedChangesWarning
      open={isWarningOpen}
      onConfirm={() => {
        router.push(backHref ?? '/');
        setIsWarningOpen(false);
      }}
      onCancel={() => setIsWarningOpen(false)}
    />
  );
}
